// src/pages/provider/ProviderSettingsPage.jsx
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import {
  User,
  Globe,
  Lock,
  Mail,
  MessageCircle,
  HelpCircle,
  FileText,
  LogOut,
  Trash2,
  CheckCircle,
  XCircle,
  ChevronRight,
  Eye,
  EyeOff,
  Settings
} from 'lucide-react';

import { useLanguage, languages } from '../../contexts/LanguageContext';
import useTranslation from '../../hooks/useTranslation';
import colors from '../../constants/colors';
import styles from '../../styles/ProviderSettingsPage.module.css';

// Redux imports - FIXED: Make sure changePassword is imported correctly
import { 
  selectUser, 
  selectIsAuthenticated,
  logout,
  clearAuthErrors,
  resetAuthState,
  changePassword,  // This should work now
  selectAuthLoading,
  selectAuthError
} from '../../store/slices/authSlice';

import {
  selectCurrentProvider,
  selectProviderLoading,
  selectProviderUpdating,
  selectProviderUpdateSuccess,
  selectProviderUpdateError,
  updateProvider,
  clearCurrentProvider,
  resetUpdateState,
  clearErrors
} from '../../store/slices/providerSlice';
import {
  getReviewStatistics,
  selectReviewStatistics,
  selectReviewsLoading
} from '../../store/slices/serviceProviderReviewSlice';
import { selectProfile } from '../../store/slices/profileSlice';

const ProviderSettingsPage = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { currentLanguage, changeLanguage, isTranslating } = useLanguage();
  
  // Redux selectors
  const user = useSelector(selectUser);
  const isAuthenticated = useSelector(selectIsAuthenticated);
  const provider_profile = useSelector(selectProfile);
  const provider = provider_profile?.providerProfile;
  const isProviderLoading = useSelector(selectProviderLoading);
  const isUpdating = useSelector(selectProviderUpdating);
  const updateSuccess = useSelector(selectProviderUpdateSuccess);
  const updateError = useSelector(selectProviderUpdateError);
  const isAuthLoading = useSelector(selectAuthLoading);
  const authError = useSelector(selectAuthError);
  const reviewStats = useSelector(selectReviewStatistics);
  const reviewsLoading = useSelector(selectReviewsLoading);
  
  const [emailUpdates, setEmailUpdates] = useState(true);
  const [showChangePasswordModal, setShowChangePasswordModal] = useState(false);
  const [showLanguageModal, setShowLanguageModal] = useState(false);
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmNewPassword, setConfirmNewPassword] = useState('');
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmNewPassword, setShowConfirmNewPassword] = useState(false);
  const [isChangingPassword, setIsChangingPassword] = useState(false);
  const [passwordError, setPasswordError] = useState(null);
  const [passwordSuccess, setPasswordSuccess] = useState(false);

  // ======================================================
  // TRANSLATIONS
  // ======================================================
  const { translatedText: settings } = useTranslation('Settings');
  const { translatedText: account } = useTranslation('Account');
  const { translatedText: editProfile } = useTranslation('Edit Profile');
  const { translatedText: changePasswordLabel } = useTranslation('Change Password');
  const { translatedText: emailUpdatesLabel } = useTranslation('Email Updates');
  const { translatedText: support } = useTranslation('Support');
  const { translatedText: contactSupport } = useTranslation('Contact Support');
  const { translatedText: helpCenter } = useTranslation('Help Center');
  const { translatedText: termsPrivacy } = useTranslation('Terms & Privacy Policy');
  const { translatedText: performance } = useTranslation('Performance');
  const { translatedText: rating } = useTranslation('Rating');
  const { translatedText: reviews } = useTranslation('Reviews');
  const { translatedText: jobs } = useTranslation('Jobs');
  const { translatedText: completion } = useTranslation('Completion');
  const { translatedText: providerTips } = useTranslation('Provider Tips');
  const { translatedText: tip1 } = useTranslation('Keep your availability updated to get more job requests');
  const { translatedText: tip2 } = useTranslation('Respond to client inquiries within 15 minutes');
  const { translatedText: tip3 } = useTranslation('Add photos of your work to showcase your skills');
  const { translatedText: tip4 } = useTranslation('Maintain a 4.5+ rating to get featured');
  const { translatedText: dangerZone } = useTranslation('Danger Zone');
  const { translatedText: logoutLabel } = useTranslation('Logout');
  const { translatedText: deleteAccount } = useTranslation('Delete Account');
  const { translatedText: haitianServices } = useTranslation('Haitian Services Marketplace');
  const { translatedText: connectingHaitians } = useTranslation('Connecting Haitians Worldwide');
  const { translatedText: madeWithLove } = useTranslation('Made with ❤️ in Haiti');
  
  // Language Translations
  const { translatedText: language } = useTranslation('Language');
  const { translatedText: selectLanguage } = useTranslation('Select Language');
  const { translatedText: currentLanguageLabel } = useTranslation('Current Language');
  const { translatedText: updatingLanguage } = useTranslation('Updating language...');
  
  // Password Modal Translations
  const { translatedText: currentPasswordLabel } = useTranslation('Current Password');
  const { translatedText: currentPasswordPlaceholder } = useTranslation('Enter current password');
  const { translatedText: newPasswordLabel } = useTranslation('New Password');
  const { translatedText: confirmNewPasswordLabel } = useTranslation('Confirm New Password');
  const { translatedText: newPasswordPlaceholder } = useTranslation('Enter new password (min 6 characters)');
  const { translatedText: confirmPasswordPlaceholder } = useTranslation('Confirm new password');
  const { translatedText: updatePassword } = useTranslation('Update Password');
  
  // Footer Translations
  const { translatedText: homeTitle } = useTranslation('Haitian Services');
  const { translatedText: quickLinks } = useTranslation('Quick Links');
  const { translatedText: findProviders } = useTranslation('Find Providers');
  const { translatedText: supportUs } = useTranslation('Support Us');
  const { translatedText: helpAndSupport } = useTranslation('Help & Support');
  const { translatedText: aboutUs } = useTranslation('About Us');
  const { translatedText: forProviders } = useTranslation('For Providers');
  const { translatedText: becomeAProvider } = useTranslation('Become a Provider');
  const { translatedText: providerLogin } = useTranslation('Provider Login');
  const { translatedText: dashboardLabel } = useTranslation('Dashboard');
  const { translatedText: legal } = useTranslation('Legal');
  const { translatedText: termsOfService } = useTranslation('Terms of Service');
  const { translatedText: privacyPolicy } = useTranslation('Privacy Policy');
  const { translatedText: cookiePolicy } = useTranslation('Cookie Policy');
  
  // Alert Translations
  const { translatedText: logoutTitle } = useTranslation('Logout');
  const { translatedText: logoutMessage } = useTranslation('Are you sure you want to logout?');
  const { translatedText: cancel } = useTranslation('Cancel');
  const { translatedText: deleteAccountTitle } = useTranslation('Delete Account');
  const { translatedText: deleteAccountMessage } = useTranslation('Are you sure you want to delete your provider account? This action cannot be undone and will remove all your services and reviews.');
  const { translatedText: deleteLabel } = useTranslation('Delete');
  const { translatedText: accountDeleted } = useTranslation('Account Deleted');
  const { translatedText: accountDeletedMessage } = useTranslation('Your provider account has been deleted successfully');
  const { translatedText: error } = useTranslation('Error');
  const { translatedText: fillAllFields } = useTranslation('Please fill in all fields');
  const { translatedText: passwordMinLength } = useTranslation('Password must be at least 6 characters');
  const { translatedText: passwordMismatch } = useTranslation('Passwords do not match');
  const { translatedText: success } = useTranslation('Success');
  const { translatedText: passwordChanged } = useTranslation('Password changed successfully!');
  const { translatedText: active } = useTranslation('Active');

  // ======================================================
  // LOAD REVIEW STATISTICS
  // ======================================================
  useEffect(() => {
    if (isAuthenticated && provider?._id) {
      dispatch(getReviewStatistics(provider._id));
    }
  }, [dispatch, isAuthenticated, provider?._id]);

  // ======================================================
  // HANDLE LOGOUT - Using Redux
  // ======================================================
  const handleLogout = () => {
    if (window.confirm(`${logoutTitle}\n\n${logoutMessage}`)) {
      dispatch(logout())
        .unwrap()
        .then(() => {
          navigate('/login');
        })
        .catch((err) => {
          console.error('Logout error:', err);
          navigate('/login');
        });
    }
  };

  // ======================================================
  // HANDLE DELETE ACCOUNT
  // ======================================================
  const handleDeleteAccount = () => {
    if (window.confirm(`${deleteAccountTitle}\n\n${deleteAccountMessage}`)) {
      alert(`${accountDeleted}\n\n${accountDeletedMessage}`);
      dispatch(logout())
        .unwrap()
        .then(() => {
          navigate('/login');
        })
        .catch(() => {
          navigate('/login');
        });
    }
  };

const handleChangePassword = async () => {
  // Validate all fields
  if (!currentPassword || !newPassword || !confirmNewPassword) {
    alert(`${error}\n\n${fillAllFields}`);
    return;
  }
  if (newPassword.length < 6) {
    alert(`${error}\n\n${passwordMinLength}`);
    return;
  }
  if (newPassword !== confirmNewPassword) {
    alert(`${error}\n\n${passwordMismatch}`);
    return;
  }

  setIsChangingPassword(true);
  setPasswordError(null);
  setPasswordSuccess(false);

  // Get current language from context or use default
  const language = currentLanguage || 'ht';

  console.log('🔄 Changing password with language:', language);

  try {
    const result = await dispatch(changePassword({ 
      currentPassword: currentPassword,
      newPassword: newPassword,
      language: language  // Pass the language here
    })).unwrap();

    console.log('✅ Password changed successfully:', result);

    setPasswordSuccess(true);
    alert(`${success}\n\n${passwordChanged}`);
    setShowChangePasswordModal(false);
    setCurrentPassword('');
    setNewPassword('');
    setConfirmNewPassword('');
    setIsChangingPassword(false);
    dispatch(resetAuthState());
  } catch (err) {
    console.error('❌ Password change error:', err);
    const errorMessage = typeof err === 'string' ? err : err?.message || 'Failed to change password';
    setPasswordError(errorMessage);
    alert(`${error}\n\n${errorMessage}`);
    setIsChangingPassword(false);
  }
};

  // ======================================================
  // HANDLE LANGUAGE SELECT
  // ======================================================
  const handleLanguageSelect = async (languageCode) => {
    if (languageCode !== currentLanguage) {
      await changeLanguage(languageCode);
    }
    setShowLanguageModal(false);
  };

  // ======================================================
  // GET CURRENT LANGUAGE LABEL
  // ======================================================
  const getCurrentLanguageLabel = () => {
    const lang = languages.find(l => l.code === currentLanguage);
    return lang ? `${lang.flag} ${lang.name}` : '🇺🇸 English';
  };

  // ======================================================
  // CLEANUP ON UNMOUNT
  // ======================================================
  useEffect(() => {
    return () => {
      dispatch(resetUpdateState());
      dispatch(clearErrors());
      dispatch(clearAuthErrors());
      dispatch(resetAuthState());
    };
  }, [dispatch]);

  // ======================================================
  // GET PERFORMANCE DATA
  // ======================================================
  const getPerformanceData = () => {
    const avgRating = reviewStats?.averageRating || provider?.rating || user?.rating || 0;
    const totalReviews = reviewStats?.totalReviews || provider?.totalReviews || user?.totalReviews || 0;
    const totalJobs = provider?.totalJobsCompleted || user?.totalJobsCompleted || 0;
    
    let completionRate = '0%';
    if (totalJobs > 0) {
      const rate = (totalReviews / totalJobs) * 100;
      completionRate = `${Math.min(Math.round(rate), 100)}%`;
    } else if (provider?.completionRate) {
      completionRate = typeof provider.completionRate === 'number' 
        ? `${provider.completionRate}%` 
        : provider.completionRate;
    } else if (user?.completionRate) {
      completionRate = typeof user.completionRate === 'number'
        ? `${user.completionRate}%`
        : user.completionRate;
    }

    return {
      rating: avgRating.toFixed(1),
      reviews: totalReviews,
      jobs: totalJobs,
      completion: completionRate
    };
  };

  const performanceData = getPerformanceData();

  // ======================================================
  // SETTINGS SECTION COMPONENT
  // ======================================================
  const SettingsSection = ({ title, children }) => (
    <div className={styles.settingsSection}>
      <h6 className={styles.settingsSectionTitle}>{title}</h6>
      <div className={styles.settingsSectionContent}>
        {children}
      </div>
    </div>
  );

  // ======================================================
  // SETTINGS ITEM COMPONENT
  // ======================================================
  const SettingsItem = ({ icon: Icon, label, type = 'switch', value, onValueChange, onPress, rightText, color }) => {
    const renderRightContent = () => {
      if (type === 'switch') {
        return (
          <label className={styles.switch}>
            <input
              type="checkbox"
              checked={value}
              onChange={(e) => onValueChange(e.target.checked)}
            />
            <span className={styles.slider}></span>
          </label>
        );
      } else if (type === 'language') {
        return (
          <div className={styles.settingsItemRight}>
            <span className={styles.settingsItemRightText}>{rightText}</span>
            <ChevronRight size={18} color={colors.gray400} />
          </div>
        );
      } else {
        return <ChevronRight size={20} color={colors.gray400} />;
      }
    };

    return (
      <div 
        className={`${styles.settingsItem} ${type !== 'switch' ? styles.clickable : ''}`}
        onClick={type !== 'switch' ? onPress : undefined}
      >
        <div className={styles.settingsItemLeft}>
          <Icon size={20} color={color || colors.primary} />
          <span className={styles.settingsItemLabel}>{label}</span>
        </div>
        {renderRightContent()}
      </div>
    );
  };

  // ======================================================
  // MAIN RENDER
  // ======================================================
  return (
    <div className={styles.container}>
      <div className={styles.settingsHeader}>
        <h2>{settings}</h2>
      </div>

      <div className={styles.settingsContent}>
        {/* Provider Profile Summary */}
        <div className={styles.profileSummary}>
          <div className={styles.profileAvatar} style={{ backgroundColor: colors.primary }}>
            <span className={styles.profileInitial}>
              {user?.name?.charAt(0) || user?.businessName?.charAt(0) || 'P'}
            </span>
          </div>
          <div className={styles.profileInfo}>
            <div className={styles.profileName}>{user?.name || user?.businessName || 'Provider'}</div>
            <div className={styles.profileBusiness}>{user?.businessName || 'Service Provider'}</div>
            <span className={styles.profileService}>{user?.serviceType || provider?.serviceType || 'Service'}</span>
          </div>
          <div className={styles.profileStatus}>
            <span className={styles.statusDot} style={{ backgroundColor: colors.success }} />
            <span className={styles.statusText} style={{ color: colors.success }}>{active}</span>
          </div>
        </div>

        {/* Language Settings */}
        <SettingsSection title={language}>
          <SettingsItem
            icon={Globe}
            label={language}
            type="language"
            onPress={() => setShowLanguageModal(true)}
            rightText={getCurrentLanguageLabel()}
            color={colors.primary}
          />
        </SettingsSection>

        {/* Account Settings */}
        <SettingsSection title={account}>
          <SettingsItem
            icon={User}
            label={editProfile}
            type="link"
            onPress={() => navigate('/provider-profile')}
          />
          <SettingsItem
            icon={Lock}
            label={changePasswordLabel}
            type="link"
            onPress={() => setShowChangePasswordModal(true)}
          />
          <SettingsItem
            icon={Mail}
            label={emailUpdatesLabel}
            type="switch"
            value={emailUpdates}
            onValueChange={setEmailUpdates}
          />
        </SettingsSection>

        {/* Support Section */}
        <SettingsSection title={support}>
          <SettingsItem
            icon={MessageCircle}
            label={contactSupport}
            type="link"
            onPress={() => alert(`${contactSupport}\n\nOur support team will assist you shortly`)}
          />
          <SettingsItem
            icon={HelpCircle}
            label={helpCenter}
            type="link"
            onPress={() => window.open('https://example.com/help', '_blank')}
          />
          <SettingsItem
            icon={FileText}
            label={termsPrivacy}
            type="link"
            onPress={() => window.open('https://example.com/terms', '_blank')}
          />
        </SettingsSection>

        {/* Performance Stats - Based on Review Statistics */}
        <SettingsSection title={performance}>
          {reviewsLoading ? (
            <div className={styles.loadingContainer}>
              <div className={styles.loadingSpinner}></div>
              <span>Loading performance data...</span>
            </div>
          ) : (
            <div className={styles.performanceContainer}>
              <div className={styles.performanceItem}>
                <div className={styles.performanceValue}>{performanceData.rating}</div>
                <div className={styles.performanceLabel}>{rating}</div>
              </div>
              <div className={styles.performanceDivider} />
              <div className={styles.performanceItem}>
                <div className={styles.performanceValue}>{performanceData.reviews}</div>
                <div className={styles.performanceLabel}>{reviews}</div>
              </div>
              <div className={styles.performanceDivider} />
              <div className={styles.performanceItem}>
                <div className={styles.performanceValue}>{performanceData.jobs}</div>
                <div className={styles.performanceLabel}>{jobs}</div>
              </div>
              <div className={styles.performanceDivider} />
              <div className={styles.performanceItem}>
                <div className={styles.performanceValue}>{performanceData.completion}</div>
                <div className={styles.performanceLabel}>{completion}</div>
              </div>
            </div>
          )}
        </SettingsSection>

        {/* Provider Tips */}
        <div className={styles.tipsContainer}>
          <h6 className={styles.tipsTitle}>💡 {providerTips}</h6>
          {[tip1, tip2, tip3, tip4].map((tip, index) => (
            <div key={index} className={styles.tipItem}>
              <CheckCircle size={16} color={colors.success} />
              <span className={styles.tipText}>{tip}</span>
            </div>
          ))}
        </div>

        {/* Danger Zone */}
        <SettingsSection title={dangerZone}>
          <button className={styles.logoutButton} onClick={handleLogout} disabled={isAuthLoading}>
            <LogOut size={22} color={colors.error} />
            <span>{isAuthLoading ? 'Logging out...' : logoutLabel}</span>
          </button>
          <button className={styles.deleteButton} onClick={handleDeleteAccount}>
            <Trash2 size={22} color={colors.error} />
            <span>{deleteAccount}</span>
          </button>
        </SettingsSection>
      </div>

      {/* Footer - Same as before */}
      <footer className={styles.footerFull}>
        <div className={styles.footerContent}>
          <div className={styles.footerGridFull}>
            {/* Brand Column */}
            <div className={styles.footerBrandColumn}>
              <div className={styles.footerBrandContainer}>
                <div className={styles.footerLogoWrapper}>
                  <img 
                    src="/logo/logo_hsm.png" 
                    alt="Haitian Services"
                    className={styles.footerLogo}
                  />
                </div>
                <div>
                  <div className={styles.footerBrandName}>{homeTitle}</div>
                  <div className={styles.footerBrandDescription}>{connectingHaitians}</div>
                </div>
              </div>
              <div className={styles.footerSocialLinks}>
                <a href="#" target="_blank" rel="noopener noreferrer" className={styles.footerSocialIcon}>
                  <img src="https://s.magecdn.com/social/tc-facebook.svg" alt="Facebook" width="20" height="20" />
                </a>
                <a href="#" target="_blank" rel="noopener noreferrer" className={styles.footerSocialIcon}>
                  <img src="https://s.magecdn.com/social/tc-instagram.svg" alt="Instagram" width="20" height="20" />
                </a>
                <a href="#" target="_blank" rel="noopener noreferrer" className={styles.footerSocialIcon}>
                  <img src="https://s.magecdn.com/social/tc-twitter.svg" alt="Twitter" width="20" height="20" />
                </a>
                <a href="#" target="_blank" rel="noopener noreferrer" className={styles.footerSocialIcon}>
                  <img src="https://s.magecdn.com/social/tc-youtube.svg" alt="YouTube" width="20" height="20" />
                </a>
              </div>
            </div>

            {/* Quick Links - TRANSLATED */}
            <div className={styles.footerLinksColumn}>
              <h3 className={styles.footerHeading}>{quickLinks}</h3>
              <ul className={styles.footerLinks}>
                <li><a href="/providers">{findProviders}</a></li>
                <li><a href="/donation">{supportUs}</a></li>
                <li><a href="/help-support">{helpAndSupport}</a></li>
                <li><a href="/about">{aboutUs}</a></li>
              </ul>
            </div>

            {/* For Providers - TRANSLATED */}
            <div className={styles.footerLinksColumn}>
              <h3 className={styles.footerHeading}>{forProviders}</h3>
              <ul className={styles.footerLinks}>
                <li><a href="/register">{becomeAProvider}</a></li>
                <li><a href="/login">{providerLogin}</a></li>
                <li><a href="/provider-dashboard">{dashboardLabel}</a></li>
                <li><a href="/provider-settings">{settings}</a></li>
              </ul>
            </div>

            {/* Legal - TRANSLATED */}
            <div className={styles.footerLinksColumn}>
              <h3 className={styles.footerHeading}>{legal}</h3>
              <ul className={styles.footerLinks}>
                <li><a href="/terms">{termsOfService}</a></li>
                <li><a href="/privacy">{privacyPolicy}</a></li>
                <li><a href="/cookies">{cookiePolicy}</a></li>
              </ul>
            </div>
          </div>

          {/* Footer Bottom */}
          <div className={styles.footerDividerFull}></div>
          <div className={styles.footerBottomFull}>
            <div className={styles.footerFlag}>
              <div className={styles.flagStripes}>
                <div className={`${styles.stripe} ${styles.stripeBlue}`}></div>
                <div className={`${styles.stripe} ${styles.stripeRed}`}></div>
              </div>
            </div>
            <div className={styles.footerBottomText}>
              © 2026 {homeTitle}. {madeWithLove}
            </div>
          </div>
        </div>
      </footer>

      {/* Change Password Modal - FIXED */}
      {showChangePasswordModal && (
        <div className={styles.modalOverlay} onClick={() => setShowChangePasswordModal(false)}>
          <div className={styles.modalContent} onClick={(e) => e.stopPropagation()}>
            <div className={styles.modalHeader}>
              <h5 className={styles.modalTitle}>{changePasswordLabel}</h5>
              <button className={styles.modalClose} onClick={() => setShowChangePasswordModal(false)}>
                <XCircle size={24} />
              </button>
            </div>
            <div className={styles.modalBody}>
              {/* Current Password Field */}
              <div className={styles.formGroup}>
                <label className={styles.formLabel}>{currentPasswordLabel}</label>
                <div className={styles.passwordInputWrapper}>
                  <input
                    type={showCurrentPassword ? 'text' : 'password'}
                    className={styles.formInput}
                    placeholder={currentPasswordPlaceholder}
                    value={currentPassword}
                    onChange={(e) => setCurrentPassword(e.target.value)}
                    disabled={isChangingPassword}
                  />
                  <button 
                    className={styles.eyeButton}
                    onClick={() => setShowCurrentPassword(!showCurrentPassword)}
                    type="button"
                  >
                    {showCurrentPassword ? <EyeOff size={20} color={colors.gray400} /> : <Eye size={20} color={colors.gray400} />}
                  </button>
                </div>
              </div>

              {/* New Password Field */}
              <div className={styles.formGroup}>
                <label className={styles.formLabel}>{newPasswordLabel}</label>
                <div className={styles.passwordInputWrapper}>
                  <input
                    type={showNewPassword ? 'text' : 'password'}
                    className={styles.formInput}
                    placeholder={newPasswordPlaceholder}
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    disabled={isChangingPassword}
                  />
                  <button 
                    className={styles.eyeButton}
                    onClick={() => setShowNewPassword(!showNewPassword)}
                    type="button"
                  >
                    {showNewPassword ? <EyeOff size={20} color={colors.gray400} /> : <Eye size={20} color={colors.gray400} />}
                  </button>
                </div>
              </div>

              {/* Confirm New Password Field */}
              <div className={styles.formGroup}>
                <label className={styles.formLabel}>{confirmNewPasswordLabel}</label>
                <div className={styles.passwordInputWrapper}>
                  <input
                    type={showConfirmNewPassword ? 'text' : 'password'}
                    className={styles.formInput}
                    placeholder={confirmPasswordPlaceholder}
                    value={confirmNewPassword}
                    onChange={(e) => setConfirmNewPassword(e.target.value)}
                    disabled={isChangingPassword}
                  />
                  <button 
                    className={styles.eyeButton}
                    onClick={() => setShowConfirmNewPassword(!showConfirmNewPassword)}
                    type="button"
                  >
                    {showConfirmNewPassword ? <EyeOff size={20} color={colors.gray400} /> : <Eye size={20} color={colors.gray400} />}
                  </button>
                </div>
              </div>

              {passwordError && (
                <div className={styles.errorMessage}>
                  <span className={styles.errorText}>{passwordError}</span>
                </div>
              )}

              {passwordSuccess && (
                <div className={styles.successMessage}>
                  <span className={styles.successText}>{passwordChanged}</span>
                </div>
              )}

              <button 
                className={styles.submitButton} 
                onClick={handleChangePassword}
                disabled={isChangingPassword || isAuthLoading}
              >
                {isChangingPassword || isAuthLoading ? (
                  <div className={styles.loadingSpinnerSmall}></div>
                ) : (
                  updatePassword
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Language Modal */}
      {showLanguageModal && (
        <div className={styles.modalOverlay} onClick={() => setShowLanguageModal(false)}>
          <div className={`${styles.modalContent} ${styles.languageModal}`} onClick={(e) => e.stopPropagation()}>
            <div className={styles.modalHeader}>
              <h5 className={styles.modalTitle}>{selectLanguage}</h5>
              <button className={styles.modalClose} onClick={() => setShowLanguageModal(false)}>
                <XCircle size={24} />
              </button>
            </div>
            <div className={styles.modalBody}>
              <p className={styles.modalSubtitle}>
                {currentLanguageLabel}: {getCurrentLanguageLabel()}
              </p>
              {languages.map((item) => (
                <button
                  key={item.code}
                  className={`${styles.languageOption} ${currentLanguage === item.code ? styles.active : ''}`}
                  onClick={() => handleLanguageSelect(item.code)}
                  disabled={isTranslating}
                >
                  <div className={styles.languageOptionLeft}>
                    <span className={styles.languageFlag}>{item.flag}</span>
                    <div>
                      <div className={styles.languageName}>{item.name}</div>
                      <div className={styles.languageCode}>{item.code.toUpperCase()}</div>
                    </div>
                  </div>
                  {currentLanguage === item.code && (
                    <CheckCircle size={22} color={colors.primary} />
                  )}
                </button>
              ))}
              {isTranslating && (
                <div className={styles.loadingContainer}>
                  <div className={styles.loadingSpinner}></div>
                  <span>{updatingLanguage}</span>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ProviderSettingsPage;